"use client";

import { useLayoutEffect, useRef } from "react";
import { detectLocale } from "@/lib/detect-locale";
import { useAppStore } from "@/lib/store/app";

/**
 * Reconciles the server-seeded default locale (see lib/store/app.ts) with the
 * client's real locale — cookie first, then browser language — once on mount.
 *
 * Runs in a layout effect so the swap lands before the browser paints the
 * post-hydration frame, and toggles isLanguageLoading around it so any
 * remaining flash of the wrong-locale text is masked behind the existing
 * FullScreenLoader rather than shown to the user. A useLayoutEffect swap
 * plus the loader overlay is the cheapest combination the existing loader
 * machinery already supports; it needs no new UI.
 *
 * Call once, from the app root (RootLayoutContent), so it reconciles for the
 * whole tree — not per-component. In production the effect body runs exactly
 * once; React 18 StrictMode (enabled in dev) invokes the mount effect twice,
 * but the hasSynced ref survives the double-invocation (same component
 * instance, not remounted), so the guard still caps the reconciliation to a
 * single run even in dev.
 */
export function useLocaleSync(): void {
  const locale = useAppStore((state) => state.locale);
  const setLocale = useAppStore((state) => state.setLocale);
  const setLanguageLoading = useAppStore((state) => state.setLanguageLoading);
  const hasSynced = useRef(false);

  useLayoutEffect(() => {
    if (hasSynced.current) return;
    hasSynced.current = true;

    const detected = detectLocale();
    if (detected === locale) return;

    setLanguageLoading(true);
    setLocale(detected);
    // Not cancelled on cleanup: this fires exactly once (hasSynced guards
    // re-entry), and the store setter is harmless to call after unmount.
    requestAnimationFrame(() => setLanguageLoading(false));
  }, [locale, setLocale, setLanguageLoading]);
}
